import { useEffect, useState } from "react";
import axios from "axios";
import { Handshake, ArrowRight } from "@phosphor-icons/react";
import { useBranding } from "@/contexts/BrandingContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PartnerLogo({ partner }) {
  const [imgErr, setImgErr] = useState(false);
  const initials = partner.name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");

  return (
    <a
      href={partner.website_url}
      target="_blank"
      rel="noopener noreferrer"
      title={`${partner.name} — visit website`}
      data-testid={`partner-${partner.id}`}
      className="group flex flex-col items-center justify-center gap-2.5 px-5 py-5 rounded-2xl bg-stone-900/40 border border-stone-800/60 hover:border-emerald-700/50 hover:bg-stone-900 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-emerald-900/20 min-w-[140px]"
    >
      <div className="h-14 w-full flex items-center justify-center">
        {partner.logo_url && !imgErr ? (
          <img
            src={partner.logo_url}
            alt={`${partner.name} logo`}
            loading="lazy"
            onError={() => setImgErr(true)}
            className="max-h-14 max-w-[120px] object-contain opacity-80 group-hover:opacity-100 transition-opacity"
          />
        ) : (
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-600/30 to-stone-700/40 flex items-center justify-center text-emerald-300 font-heading text-lg font-bold border border-emerald-800/40">
            {initials || "?"}
          </div>
        )}
      </div>
      <p className="text-xs text-stone-400 group-hover:text-white font-body text-center max-w-[140px] truncate">
        {partner.name}
      </p>
    </a>
  );
}

export default function PartnersSection() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const { branding } = useBranding();
  const partnerEmail = branding?.contact_email || "info@conexusfoundation.org";

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/partners`)
      .then((r) => {
        if (mounted) setPartners(r.data?.partners || []);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  // Show the "Become a partner" CTA even when the logo grid is empty — this
  // keeps the invitation to partner with us always visible on the home page.
  const hasPartners = !loading && partners.length > 0;

  return (
    <section
      data-testid="partners-section"
      className="bg-stone-950 border-y border-stone-900/80 py-16 sm:py-20"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col items-center text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-600/10 border border-emerald-700/30 mb-4">
            <Handshake size={14} weight="duotone" className="text-emerald-400" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-heading font-semibold">
              Partner With Us
            </span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">
            Building Earth&apos;s climate stack, together
          </h2>
          <p className="text-sm text-stone-400 mt-3 max-w-xl font-body">
            Conexus Foundation collaborates with research institutes, NGOs, satellite data providers,
            and climate-finance partners around the world.
            {hasPartners ? " Click any logo to visit their site." : ""}
          </p>
        </div>

        {hasPartners && (
          <div className="flex flex-wrap items-stretch justify-center gap-3 sm:gap-4 mb-10">
            {partners.map((p) => (
              <PartnerLogo key={p.id} partner={p} />
            ))}
          </div>
        )}

        {/* Always-visible CTA to become a partner */}
        <div className="max-w-3xl mx-auto bg-gradient-to-br from-emerald-900/40 via-stone-900 to-stone-950 rounded-3xl border border-emerald-700/30 p-8 sm:p-10 text-center"
             data-testid="become-partner-cta">
          <h3 className="font-heading text-xl sm:text-2xl font-bold text-white mb-3">
            Become a partner
          </h3>
          <p className="text-sm text-stone-400 font-body max-w-xl mx-auto">
            NGOs, universities, data providers, and climate-finance organisations — write to us at
            {" "}
            <a href={`mailto:${partnerEmail}?subject=Partnership%20Inquiry%20—%20Conexus%20Foundation`}
               className="text-emerald-400 hover:text-emerald-300 font-mono">
              {partnerEmail}
            </a>
            {" "}or use the contact form below. We reply within two business days.
          </p>
          <a href={`mailto:${partnerEmail}?subject=Partnership%20Inquiry%20—%20Conexus%20Foundation`}
             data-testid="become-partner-btn"
             className="inline-flex items-center gap-2 mt-5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-colors">
            <Handshake size={14} weight="bold" /> Start a partnership conversation
            <ArrowRight size={14} weight="bold" />
          </a>
        </div>
      </div>
    </section>
  );
}
